import React, { useEffect, useState } from "react";
import { CollaboratorProfileCard } from "../CollaboratorProfileCard";
import { CollaboratorTraining } from "../../../types/api/collaborators.type";
import { BodyTyp, TitlesTyp } from "../../Typography/Typography";
import { FontWeightEnum, IconSizeEnum } from "../../../types/AppTypes";
import {
  FORMATION_COURSE_LABEL,
  FORMATION_LABEL,
  LANGUAGE_COURSE_LABEL,
  PATH_COURSE_LABEL,
  TECH_COURSE_LABEL,
} from "../../../constants/Dictionary";
import { Select, SelectVariantEnum } from "../../core/Select/Select";
import { InputSizes } from "../../core/Input/Input";
import { IconManager } from "../../core/IconManager/IconManager";
import { theme } from "../../../constants/StyledComponents";
import { CircleProgressBar } from "../../CircleProgressBar/CircleProgressBar";
import { ReactComponent as CodeIcon } from "../../../icons/code.svg";
import { ReactComponent as GlobeIcon } from "../../../icons/globe.svg";
import { ReactComponent as BookOpenIcon } from "../../../icons/bookOpen.svg";
import { ReactComponent as TrainingIcon } from "../../../icons/training.svg";
import {
  StyledFormationRow,
  StyledFormationCard,
} from "./FormationCard.styled";

type FormationCardProps = {
  training: CollaboratorTraining[];
};

type FormationRowProps = {
  icon: React.ReactNode;
  label: string;
  achievedGoal: string;
};

const FormationRow = ({ icon, label, achievedGoal }: FormationRowProps) => {
  return (
    <StyledFormationRow>
      <IconManager
        color={theme.colors.purple}
        size={IconSizeEnum.XSMALL}
        marginRight={8}
      >
        {icon}
      </IconManager>
      <div className="label">
        <BodyTyp fontWeight={FontWeightEnum.MEDIUM} variant="3">
          {label}
        </BodyTyp>
      </div>
      <BodyTyp fontWeight={FontWeightEnum.SEMIBOLD} variant="3">
        {achievedGoal}
      </BodyTyp>
    </StyledFormationRow>
  );
};

export const FormationCard = ({ training }: FormationCardProps) => {
  const [selectedTraining, setSelectedTraining] =
    useState<CollaboratorTraining | null>(null);

  const onChangeYear = (value: number | null) => {
    if (value) {
      const selected = training.find((item) => item.year === value);
      if (selected) {
        setSelectedTraining(selected);
      }
    }
  };

  useEffect(() => {
    if (training.length > 0) {
      setSelectedTraining(training[0]);
    }
  }, [training]);

  return (
    <CollaboratorProfileCard
      content={
        <>
          <div className="card-header">
            <TitlesTyp fontWeight={FontWeightEnum.SEMIBOLD} variant="3">
              {FORMATION_LABEL}
            </TitlesTyp>
            <Select
              variant={SelectVariantEnum.DEFAULT}
              onChange={onChangeYear}
              size={InputSizes.SMALL}
              options={training.map((items) => ({
                value: items.year,
                label: items.year.toString(),
              }))}
              value={selectedTraining?.year}
              allowClear={false}
            />
          </div>
          <StyledFormationCard>
            <div className="formation-column">
              <FormationRow
                icon={<GlobeIcon />}
                label={LANGUAGE_COURSE_LABEL}
                achievedGoal={`${selectedTraining?.courses.language.completed ?? 0}/${selectedTraining?.courses.language.total ?? 0}`}
              />
              <FormationRow
                icon={<CodeIcon />}
                label={TECH_COURSE_LABEL}
                achievedGoal={`${selectedTraining?.courses.tech.completed ?? 0}/${selectedTraining?.courses.tech.total ?? 0}`}
              />
              <FormationRow
                icon={<BookOpenIcon />}
                label={PATH_COURSE_LABEL}
                achievedGoal={`${selectedTraining?.courses.path.completed ?? 0}/${selectedTraining?.courses.path.total ?? 0}`}
              />
              <FormationRow
                icon={<TrainingIcon />}
                label={FORMATION_COURSE_LABEL}
                achievedGoal={`${selectedTraining?.courses.formation.completed ?? 0}/${selectedTraining?.courses.formation.total ?? 0}`}
              />
            </div>
            <CircleProgressBar
              key={selectedTraining?.progress ?? 0}
              valuePercentage={selectedTraining?.progress ?? 0}
              size={150}
              strokeWidth={8}
              strokeColor={theme.colors.green}
              bgStrokeColor={theme.colors.grey_5}
            />
          </StyledFormationCard>
        </>
      }
    />
  );
};
